import React, { useEffect, useState } from 'react';
import { MapContainer as LeafletMap, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import type { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { StudentData } from '../utils/types';
import { getLocationFromZip, calculateDistance } from '../utils/dataUtils';
import { devLogSafe, devLog, devError } from '../utils/secureLogger';
import { getValidToken, API_URL } from '../utils/authUtils';

// Fix for default marker icon in webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore - Leaflet types are not properly exported
const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// @ts-ignore - Leaflet types are not properly exported
L.Marker.prototype.options.icon = DefaultIcon;

interface MapViewProps {
  students: StudentData[];
  darkMode: boolean;
  centerZip: string;
  radiusMiles: number;
  onStudentSelect: (student: StudentData) => void;
  zipCodeMap: Map<string, { latitude: number; longitude: number }>;
  areaCodeMap: Map<string, { latitude: number; longitude: number }>;
}

interface MapContainerProps {
  center: LatLngTuple;
  zoom: number;
  style: React.CSSProperties;
  scrollWheelZoom: boolean;
  className: string;
  children: React.ReactNode;
  whenCreated?: (map: L.Map) => void;
}

const MapContainer: React.FC<MapContainerProps> = (props) => {
  return <LeafletMap {...props} />;
};

const MapView: React.FC<MapViewProps> = ({ 
  students, 
  darkMode, 
  centerZip, 
  radiusMiles,
  onStudentSelect,
  zipCodeMap,
  areaCodeMap
}) => {
  const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
  const [center, setCenter] = useState<[number, number]>([39.8283, -98.5795]); // Center of US
  const [zoom, setZoom] = useState<number>(4); // Zoom level to show US and surrounding regions
  const [centerCoords, setCenterCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [loadingResume, setLoadingResume] = useState<string | null>(null);

  useEffect(() => {
    devLog(`MapView received ${students.length} students`);
    let processedStudents = 0;
    let studentsWithAreaCodes = 0;
    let studentsWithLocations = 0;
    
    students.forEach(student => {
      processedStudents++;
      const areaCode = getAreaCode(student.phone);
      if (!areaCode) {
        devError(`Could not extract area code from phone number for student ${student.name}: ${student.phone}`);
        return;
      }
      
      studentsWithAreaCodes++;
      const location = areaCodeMap.get(areaCode);
      if (!location) {
        devError(`No location found for area code ${areaCode} for student ${student.name}`);
        return;
      }
      
      studentsWithLocations++;
      devLog(`Student ${student.name} mapped to location: ${JSON.stringify(location)}`);
    });
    
    devLog(`Processed ${processedStudents} students`);
    devLog(`Found area codes for ${studentsWithAreaCodes} students`);
    devLog(`Found locations for ${studentsWithLocations} students`);
  }, [students, areaCodeMap]);

  useEffect(() => {
    const updateCenter = async () => {
      if (centerZip) {
        try {
          const coords = await getLocationFromZip(centerZip);
          if (coords) {
            const newCenter: [number, number] = [coords.latitude, coords.longitude];
            setCenter(newCenter);
            setCenterCoords(coords);
            setZoom(8); // Zoom in when a zip code is selected
            devLog(`Updated map center to ${JSON.stringify(newCenter)} for zip ${centerZip}`);
          }
        } catch (error) {
          devError(`Error updating map center: ${error}`);
        }
      }
    };

    updateCenter();
  }, [centerZip]);

  const handleMarkerClick = (student: StudentData) => {
    setSelectedStudent(student);
    onStudentSelect(student);
  };

  const handleViewResume = async (studentId: string) => {
    setLoadingResume(studentId);
    try {
      const token = getValidToken();
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }

      // Fetch PDF stream from authenticated backend proxy
      const response = await fetch(`${API_URL}/api/resumes/mapview/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          alert('Resume not found for this student.');
        } else if (response.status === 403) {
          alert('Access denied. Admin privileges required.');
        } else {
          alert('Unable to load resume. Please try again.');
        }
        return;
      }

      // Convert response to blob and create temporary URL
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Open PDF in new tab
      const newWindow = window.open(blobUrl, '_blank');

      // Clean up blob URL after window opens (prevents memory leaks)
      if (newWindow) {
        newWindow.addEventListener('load', () => {
          URL.revokeObjectURL(blobUrl);
        });
      } else {
        // Fallback cleanup if popup was blocked
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch (error) {
      console.error('Error opening resume:', error);
      alert('Unable to load resume. Please try again.');
    } finally {
      setLoadingResume(null);
    }
  };

  // Extract area code from phone number
  const getAreaCode = (phone: string): string | null => {
    if (!phone) return null;
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // If we have a 10-digit number, take the first 3 digits
    if (digits.length === 10) {
      return digits.substring(0, 3);
    }
    
    // If we have a 11-digit number (with country code), take digits 2-4
    if (digits.length === 11) {
      return digits.substring(1, 4);
    }
    
    // If we have a 7-digit number, we can't determine the area code
    if (digits.length < 10) {
      devError(`Phone number too short to determine area code: ${phone}`);
      return null;
    }
    
    // For any other length, try to extract the area code
    // This handles cases where the number might have extra digits
    const areaCode = digits.substring(digits.length - 10, digits.length - 7);
    if (areaCode.length === 3) {
      return areaCode;
    }
    
    devError(`Could not extract area code from phone number: ${phone}`);
    return null;
  };

  // Filter students based on radius if center coordinates and radius are provided
  const filteredStudents = centerZip && radiusMiles && centerCoords
    ? students.filter(student => {
        const areaCode = getAreaCode(student.phone);
        if (!areaCode) {
          devError(`Could not extract area code from phone number for student ${student.name}: ${student.phone}`);
          return false;
        }

        const location = areaCodeMap.get(areaCode);
        if (!location) {
          devError(`No location found for area code ${areaCode} for student ${student.name}`);
          return false;
        }
        
        const distance = calculateDistance(
          centerCoords.latitude,
          centerCoords.longitude,
          location.latitude,
          location.longitude
        );
        
        devLog(`Student ${student.name} distance: ${distance.toFixed(2)} miles`);
        return distance <= radiusMiles;
      })
    : students;

  devLog(`Total students: ${students.length}`);
  devLog(`Filtered students: ${filteredStudents.length}`);
  devLog(`Area code map size: ${areaCodeMap.size}`);

  return (
    <div className={`relative w-full h-full ${darkMode ? 'bg-gray-900' : 'bg-white'}`} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
      <LeafletMap
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}
        scrollWheelZoom={true}
        className="map-container"
      >
        <TileLayer
          url={darkMode ? 
            'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png' : 
            'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          }
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {darkMode && (
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
        )}
        <style>
          {`
            .map-container {
              overflow: hidden !important;
              background-color: ${darkMode ? '#1a202c' : '#ffffff'} !important;
            }
            .map-container .leaflet-container {
              overflow: hidden !important;
              background-color: ${darkMode ? '#1a202c' : '#ffffff'} !important;
            }
            .leaflet-popup-content-wrapper {
              font-family: 'Poppins', sans-serif;
              font-style: normal;
            }
            .leaflet-popup-content {
              font-family: 'Poppins', sans-serif;
              font-style: normal;
            }
            ${darkMode ? `
              .leaflet-popup-content-wrapper {
                background-color: #2d3748;
                color: #ffffff;
              }
              .leaflet-popup-tip {
                background-color: #2d3748;
              }
              .leaflet-control-zoom {
                background-color: #2d3748;
                color: #ffffff;
              }
              .leaflet-control-zoom a {
                color: #ffffff;
                background-color: #2d3748;
              }
              .leaflet-control-zoom a:hover {
                background-color: #4a5568;
              }
            ` : ''}
          `}
        </style>
        
        {centerCoords && radiusMiles && (
          <Circle
            center={[centerCoords.latitude, centerCoords.longitude]}
            radius={radiusMiles * 1609.34} // Convert miles to meters
            pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.1 }}
          />
        )}
        
        {filteredStudents.map((student) => {
          const areaCode = getAreaCode(student.phone);
          if (!areaCode) {
            devError(`Could not extract area code from phone number for student ${student.name}: ${student.phone}`);
            return null;
          }

          const location = areaCodeMap.get(areaCode);
          if (!location) {
            devError(`No location found for area code ${areaCode} for student ${student.name}`);
            return null;
          }
          
          return (
            <Marker
              key={`${student.Internal_ID}-${student.phone}`}
              position={[location.latitude, location.longitude]}
              eventHandlers={{
                click: () => {
                  setSelectedStudent(student);
                  onStudentSelect(student);
                },
              }}
            >
              <Popup>
                <div className="p-2">
                  <div className="flex items-center space-x-4 mb-2">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                      {student.Internal_ID ? (
                        <img
                          src={`/headshots/${student.Internal_ID}.png`}
                          alt={`${student.name}'s headshot`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // If image fails to load, show a default avatar
                            e.currentTarget.src = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300">
                          <span className="text-gray-500">No Photo</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{student.name}</h3>
                      <p className="text-gray-600 dark:text-gray-300">{student.university}</p>
                      <p className="text-gray-600 dark:text-gray-300">{student.major}</p>
                      <p className="text-gray-600 dark:text-gray-300">Year: {student.year}</p>
                      <p className="text-gray-600 dark:text-gray-300">GPA: {student.gpa}</p>
                    </div>
                  </div>
                  {student.Internal_ID ? (
                    <button
                      onClick={() => handleViewResume(student.Internal_ID)}
                      disabled={loadingResume === student.Internal_ID}
                      className={`mt-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition w-full ${
                        loadingResume === student.Internal_ID ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {loadingResume === student.Internal_ID ? 'Loading...' : 'View Resume'}
                    </button>
                  ) : (
                    <span className="text-gray-500 block mt-2">No resume on file.</span>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </LeafletMap>

      {selectedStudent && (
        <div className="absolute bottom-4 left-4 right-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg z-50">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold">{selectedStudent.name}</h3>
              <p>{selectedStudent.major}</p>
              <p>GPA: {selectedStudent.gpa}</p>
              <p>Year: {selectedStudent.year}</p>
              <p>Skills: {selectedStudent.skills.join(', ')}</p>
            </div>
            <button
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={() => setSelectedStudent(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;